import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { ReactNode } from 'react';

type UserRole = 'company' | 'cxo' | 'member' | 'admin';

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
  children?: ReactNode;
}

export default function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to finish loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has any of the allowed roles (using roles array)
  const userRoles = user?.roles || [user?.role].filter(Boolean) as UserRole[];
  
  // Check if user has allowed role
  const hasAllowedRole = !allowedRoles || allowedRoles.length === 0 || 
    userRoles.some(role => allowedRoles.includes(role));

  if (!hasAllowedRole) {
    // Redirect to first available dashboard based on user's roles
    if (userRoles.includes('admin')) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (userRoles.includes('company')) {
      return <Navigate to="/company/dashboard" replace />;
    } else if (userRoles.includes('cxo')) {
      return <Navigate to="/cxo/dashboard" replace />;
    } else if (userRoles.includes('member')) {
      return <Navigate to="/member/dashboard" replace />;
    }
  }

  // Check if user is trying to access a route for a role they don't have
  const currentPath = location.pathname;
  let requiredRole: UserRole | null = null;
  
  if (currentPath.startsWith('/admin')) requiredRole = 'admin';
  else if (currentPath.startsWith('/company')) requiredRole = 'company';
  else if (currentPath.startsWith('/cxo')) requiredRole = 'cxo';
  else if (currentPath.startsWith('/member')) requiredRole = 'member';

  // If the route requires a specific role and user doesn't have it, redirect
  if (requiredRole && !userRoles.includes(requiredRole)) {
    // Redirect to their first available dashboard
    if (userRoles.includes('admin')) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (userRoles.includes('company')) {
      return <Navigate to="/company/dashboard" replace />;
    } else if (userRoles.includes('cxo')) {
      return <Navigate to="/cxo/dashboard" replace />;
    } else if (userRoles.includes('member')) {
      return <Navigate to="/member/dashboard" replace />;
    }
  }

  // Render children if provided, otherwise Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
}
